import React, { useState, useEffect, useMemo } from 'react';
import Popup, { ToolbarItem as PopupToolbarItem } from 'devextreme-react/popup';
import { SelectBox } from 'devextreme-react/select-box';
import { Lookup, DropDownOptions } from 'devextreme-react/lookup';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import DataSource from 'devextreme/data/data_source';
import { TransactionTypes, ReplicateLotBasis } from '../../../../../../enum/Enum';

// WM Transaction Data Entry -> Options -> General Operations -> Replicate Transactions.
// Delphi uWMD0113 (frmWMD0113), "Replicate Transactions (WMD0113)".
//
// Distinct from headerOperations/replicate/Replicate.js, which is the "WM Replicate Parameters"
// dialog (Delphi WMD0127) that stamps out N copies through SPWM_Replicate.
//
// The form itself is only a parameter collector in Delphi - its single event handler just records
// which OK button was pressed. Every default and enable/disable rule lives in the caller,
// WMD0002.ShowReplicateTransactionDialog (uWMD0002.PAS:14985-15032), and is reproduced here so the
// dialog behaves the same wherever it is opened from.

// The three combo item lists, in Delphi's order. The value IS the ItemIndex, because the caller
// stores each choice as IntToStr(ItemIndex) - '0'/'1'/'2', or '-1' for "nothing selected" - and the
// downstream replicate logic branches on those exact strings.
const QUANTITY_ITEMS = [
    { id: '0', name: 'Replicate ALL Information as is (Based on the Source Transaction)' },
    { id: '1', name: 'Clear ALL Quantities to ZERO in Line Items and Locations' },
];

const LOCATION_ITEMS = [
    { id: '0', name: 'Replicate ALL Information as is (Based on the Source Transaction)' },
    { id: '1', name: 'Clear ALL Location Codes to BLANK' },
];

// Index 0 and 2 route to WMD0114 (manual lot selection); index 1 routes to WMD0115 (auto).
const LOT_ITEMS = [
    { id: ReplicateLotBasis.ExistingLot, name: 'Replicate Existing Lot, If Available' },
    { id: ReplicateLotBasis.AutoSelectLot, name: 'Auto Select Lot' },
    { id: ReplicateLotBasis.ManualLot, name: 'Manually Select Lot - Leave Lot Code Blank' },
];

// Delphi's ItemIndex := -1, i.e. the combo shows blank and the caller records '-1'.
const NO_SELECTION = '-1';

// The profile's Lot Method for shipments. 'M' preselects "Replicate Existing Lot"; anything else
// preselects "Auto Select Lot" (uWMD0002.PAS:15011-15014).
const LOT_METHOD_MANUAL = 'M';

const memoLines = [
    'If you choose header & details option the following will be replicated',
    'upon SAVING the transaction header record.',
    '',
    '    (1) All Transaction Detail Records',
    '    (2) All Transaction Sub Detail Records (If applicable)',
    '    (3) All Transaction Location Records',
    '    (4) All Transaction and Item Charge Records (If applicable)',
    '',
    'Note: No Serial Number records will be Replicated.',
];

const labelStyle = { width: '110px', fontWeight: 'bold', fontSize: '13px', flex: '0 0 110px' };
const rowStyle = { display: 'flex', alignItems: 'center', marginBottom: '8px' };

const ReplicateTransactions = ({
    visible,
    onClose,
    onOk,
    transactionType,
    shipperId,
    consigneeId,
    lotMethodShipments,
    profileId,
    customerId,
    accessToken,
}) => {
    const isShipment = transactionType === TransactionTypes.Shipment;

    const sectionHeaderStyle = {
        backgroundColor: window._env_.Color2,
        color: '#FFFFFF',
        fontWeight: 'bold',
        fontSize: '13px',
        textAlign: 'center',
        padding: '3px',
        margin: '10px 0 8px 0',
    };

    const [quantityBasis, setQuantityBasis] = useState('0');
    const [locationCodeBasis, setLocationCodeBasis] = useState('0');
    const [lotCodeBasis, setLotCodeBasis] = useState(NO_SELECTION);
    const [shipper, setShipper] = useState(null);
    const [consignee, setConsignee] = useState(null);

    // ShowReplicateTransactionDialog builds a fresh form every time, so the defaults are re-applied
    // on each open rather than surviving from the previous run.
    useEffect(() => {
        if (!visible) return;

        setQuantityBasis('0');
        setShipper(shipperId || null);
        setConsignee(consigneeId || null);

        if (isShipment) {
            // Location is cleared and locked for shipments; lot and consignee become available.
            setLocationCodeBasis(NO_SELECTION);
            setLotCodeBasis(lotMethodShipments === LOT_METHOD_MANUAL ? ReplicateLotBasis.ExistingLot : ReplicateLotBasis.AutoSelectLot);
        } else {
            setLocationCodeBasis('0');
            setLotCodeBasis(NO_SELECTION);
        }
    }, [visible, isShipment, shipperId, consigneeId, lotMethodShipments]);

    const buildStore = (url, loadParams) => new DataSource({
        store: new createStore({
            key: 'id',
            loadParams: loadParams,
            loadUrl: `${window._env_.REACT_APP_APIS}/${url}`,
            onBeforeSend: function (method, ajaxOptions) {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        }),
        paginate: true,
        pageSize: 100,
    });

    const shipperDataSource = useMemo(
        () => buildStore('wm/Transaction/GetShippers'),
        [accessToken]
    );

    // Same parameters the main form passes, so the dialog offers the same consignees as the header.
    const consigneeDataSource = useMemo(
        () => buildStore('wm/Transaction/GetConsignees', { profileId: profileId, customerId: customerId }),
        [accessToken, profileId, customerId]
    );

    // Both OK buttons close with the same parameters; only btnHeaderDetailsClick additionally sets
    // bHeaderDetailButton, which is what makes the caller replicate the detail records too.
    const submit = (headerDetails) => {
        if (onOk) {
            onOk({
                headerDetails,
                quantityBasis,
                locationCodeBasis,
                lotCodeBasis,
                shipper,
                consignee,
            });
        }
    };

    return (
        <Popup
            visible={visible}
            onHiding={onClose}
            dragEnabled={true}
            hideOnOutsideClick={false}
            showCloseButton={true}
            showTitle={true}
            title="Replicate Transactions (WMD0113)"
            container=".dx-viewport"
            width={620}
            height={560}
        >
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ ...sectionHeaderStyle, margin: '0 0 10px 0' }}>Replicate Transaction</div>

                {/* memTopHelpMessage - read-only, states what "Header & Details" will copy. */}
                <div
                    style={{
                        border: '1px solid #999',
                        padding: '8px 10px',
                        fontWeight: 'bold',
                        fontSize: '12px',
                        whiteSpace: 'pre-wrap',
                        backgroundColor: '#FFFFFF',
                        color: '#000000',
                    }}
                >
                    {memoLines.join('\n')}
                </div>

                <div style={{ flex: 1, overflowY: 'auto', paddingTop: '12px' }}>
                    <div style={rowStyle}>
                        <label style={labelStyle}>Quantity</label>
                        <SelectBox
                            dataSource={QUANTITY_ITEMS}
                            valueExpr="id"
                            displayExpr="name"
                            value={quantityBasis}
                            onValueChanged={(e) => setQuantityBasis(e.value)}
                            stylingMode="outlined"
                            width="100%"
                        />
                    </div>

                    <div style={rowStyle}>
                        <label style={labelStyle}>Shipper</label>
                        <Lookup
                            dataSource={shipperDataSource}
                            value={shipper}
                            valueExpr="id"
                            displayExpr="name"
                            searchEnabled={true}
                            showClearButton={true}
                            onValueChanged={(e) => setShipper(e.value)}
                            stylingMode="outlined"
                            width="100%"
                        >
                            <DropDownOptions showTitle={false} />
                        </Lookup>
                    </div>

                    <div style={sectionHeaderStyle}>Receipt Transactions</div>

                    <div style={rowStyle}>
                        <label style={labelStyle}>Location</label>
                        <SelectBox
                            dataSource={LOCATION_ITEMS}
                            valueExpr="id"
                            displayExpr="name"
                            value={locationCodeBasis}
                            disabled={isShipment}
                            onValueChanged={(e) => setLocationCodeBasis(e.value)}
                            stylingMode="outlined"
                            width="100%"
                        />
                    </div>

                    <div style={sectionHeaderStyle}>Shipment Transactions</div>

                    <div style={rowStyle}>
                        <label style={labelStyle}>Lot</label>
                        <SelectBox
                            dataSource={LOT_ITEMS}
                            valueExpr="id"
                            displayExpr="name"
                            value={lotCodeBasis}
                            disabled={!isShipment}
                            onValueChanged={(e) => setLotCodeBasis(e.value)}
                            stylingMode="outlined"
                            width="100%"
                        />
                    </div>

                    <div style={rowStyle}>
                        <label style={labelStyle}>Consignee</label>
                        <Lookup
                            dataSource={consigneeDataSource}
                            value={consignee}
                            valueExpr="id"
                            displayExpr="name"
                            searchEnabled={true}
                            showClearButton={true}
                            disabled={!isShipment}
                            onValueChanged={(e) => setConsignee(e.value)}
                            stylingMode="outlined"
                            width="100%"
                        >
                            <DropDownOptions showTitle={false} />
                        </Lookup>
                    </div>
                </div>
            </div>

            <PopupToolbarItem
                toolbar="top"
                location="after"
                widget="dxButton"
                options={{
                    text: 'Header & Details',
                    type: 'default',
                    stylingMode: 'contained',
                    elementAttr: { class: 'accordianButton' },
                    onClick: () => submit(true),
                }}
            />
            <PopupToolbarItem
                toolbar="top"
                location="after"
                widget="dxButton"
                options={{
                    text: 'Header Only',
                    type: 'default',
                    stylingMode: 'contained',
                    elementAttr: { class: 'accordianButton' },
                    onClick: () => submit(false),
                }}
            />
        </Popup>
    );
};

export default ReplicateTransactions;
